import { SuitSymbol } from '../common/SuitSymbol';

/**
 * Presentation helpers for the "Decision to make a 3rd level bid" sub-mode label (feature 013,
 * Session 2026-06-10). Extracted from `ProblemsPage` so the label logic is unit-testable in
 * isolation (constitution V) and reused identically by the desktop subtitle and the mobile
 * eyebrow. Pure presentation: it derives everything from the URL params handed in.
 */

/** Human-readable sub-mode names, keyed by the `subMode` URL value. */
export const THIRD_LEVEL_SUB_MODE_LABELS: Record<string, string> = {
  overcalled: 'Overcalled bids',
  precision: 'Precision opening bids',
  other: 'Other openings',
};

const SUIT_PLAIN: Record<string, string> = { C: 'clubs', D: 'diamonds', H: 'hearts', S: 'spades' };

/**
 * Spoken/plain form of an opening-bid token for screen-reader text ("1H,1S" → "1 hearts or
 * 1 spades"; "1NT" → "1 no trump"). The visible form uses {@link OpeningBidText} with glyphs.
 */
export function openingBidToPlain(value: string): string {
  return value
    .split(',')
    .map(p => p.trim())
    .filter(Boolean)
    .map(p => {
      const level = p[0];
      const strain = p.slice(1).toUpperCase();
      if (strain === 'NT' || strain === 'N') return `${level} no trump`;
      return SUIT_PLAIN[strain] ? `${level} ${SUIT_PLAIN[strain]}` : p;
    })
    .join(' or ');
}

export interface ThirdLevelSubModeDescriptor {
  /** Display name, e.g. "Overcalled bids". */
  text: string;
  /** Selected opening bid for the Precision sub-mode ("1C", "1H,1S", "1NT", …); undefined otherwise. */
  openingBid?: string;
}

/**
 * Resolve the sub-mode descriptor from the URL params, or `null` when no sub-mode applies (e.g.
 * the dev/QA by-id locator, which loads a single hand — FR-020). Only `precision` carries an
 * opening bid.
 */
export function resolveThirdLevelSubMode(
  subMode: string | null,
  openingBid: string | null,
): ThirdLevelSubModeDescriptor | null {
  if (!subMode || !THIRD_LEVEL_SUB_MODE_LABELS[subMode]) return null;
  const ob = subMode === 'precision' ? openingBid : null;
  return { text: THIRD_LEVEL_SUB_MODE_LABELS[subMode], openingBid: ob || undefined };
}

/** Plain-text label (name + spoken opening bid) for a heading's accessible name. */
export function thirdLevelSubModePlain(descriptor: ThirdLevelSubModeDescriptor | null): string {
  if (!descriptor) return '';
  return descriptor.openingBid
    ? `${descriptor.text}, ${openingBidToPlain(descriptor.openingBid)}`
    : descriptor.text;
}

/**
 * Visible opening-bid renderer: "1C" → 1♣, "1H,1S" → 1♥ / 1♠, "1NT" → 1NT. Suit glyphs reuse the
 * shared {@link SuitSymbol} so colour/typography match the rest of the app.
 */
export function OpeningBidText({ value }: { value: string }) {
  const parts = value.split(',').map(p => p.trim()).filter(Boolean);
  return (
    <>
      {parts.map((p, i) => {
        const level = p[0];
        const strain = p.slice(1).toUpperCase();
        const suit = strain === 'C' || strain === 'D' || strain === 'H' || strain === 'S' ? strain : null;
        return (
          <span key={p} className="whitespace-nowrap">
            {i > 0 && ' / '}
            {level}
            {/* Suit glyphs render visually smaller than letters, so they are set larger than
                the surrounding label to keep ♥/♦ and ♣/♠ distinguishable at this small
                subtitle size. That sizing now comes from the site-wide `.suit-glyph` rule
                (index.css) rather than a local 1.1em override — one size everywhere except
                the masthead brand (client decision, 2026-07-21). */}
            {suit ? <SuitSymbol suit={suit} /> : (strain === 'N' ? 'NT' : strain)}
          </span>
        );
      })}
    </>
  );
}

/** The visible sub-mode label content (name + optional opening-bid glyphs). */
export function ThirdLevelSubModeLabel({ descriptor }: { descriptor: ThirdLevelSubModeDescriptor }) {
  return (
    <>
      {descriptor.text}
      {descriptor.openingBid && (
        <>
          {' – '}
          <OpeningBidText value={descriptor.openingBid} />
        </>
      )}
    </>
  );
}
